import { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import { Box, Divider, FormHelperText, Grid, Typography } from '@mui/material';
import { Control, UseFormReturn } from 'react-hook-form';
import DateInput from '../../forms/DateInput';
import TimeInput from '../../forms/TimeInput';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import Checkbox from '../../forms/Checkbox';
import dateUtils from '../../../services/DateUtils';
import { DateTime, Interval } from 'luxon';
import {
  ClientSetting,
  ClientSettingOption,
} from '../../../data/models/client-settings.model';
import {
  StandingOrderReturnType,
  TripLegType,
} from '../../../data/models/reservation.model';
import SelectMenu from '../../forms/SelectMenu';
import { useFeatureStandingOrderReturnSpecification } from '../../../hooks/features/useFeatureStandingOrderReturnSpecification';
import { useAppSelector } from '../../../hooks/useAppSelector';
import * as reservationSlice from '../../../data/reservation.slice';

const PREFIX = 'StandingOrderForm';

const classes = {
  standingOrderRange: `${PREFIX}-standingOrderRange`,
  standingOrderDescription: `${PREFIX}-standingOrderDescription`,
  standingOrderGrid: `${PREFIX}-standingOrderGrid`,
  standingOrderRecap: `${PREFIX}-standingOrderRecap`,
  standingOrderRecapBold: `${PREFIX}-standingOrderRecapBold`,
  standingOrderDate: `${PREFIX}-standingOrderDate`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`& .${classes.standingOrderRange}`]: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  [`& .${classes.standingOrderDescription}`]: {
    marginTop: `-${theme.spacing(2)}`,
    marginBottom: theme.spacing(2),
    color: theme.palette.lightTextSecondary.main,
    maxWidth: 500,
  },
  [`& .${classes.standingOrderGrid}`]: {
    maxWidth: 500,
    marginTop: theme.spacing(2),
  },
  [`& .${classes.standingOrderRecap}`]: {
    lineHeight: theme.spacing(3),
    color: theme.palette.lightTextSecondary.main,
    maxWidth: 500,
    marginTop: theme.spacing(2),
  },
  [`& .${classes.standingOrderRecapBold}`]: {
    fontWeight: 700,
  },

  [`& .${classes.standingOrderDate}`]: {
    display: 'flex',
    flexDirection: 'column',
    width: 230,
  },
}));

function PickupTypeSelectMenu({
  name,
  control,
}: {
  name: string;
  control: Control<any, any, any>;
}) {
  const t = useCommonTranslation();
  const values = [
    {
      name: t('reservation.returnTypeWillCall'),
      value: StandingOrderReturnType.willCall,
    },
    {
      name: t('reservation.returnTypeTime'),
      value: StandingOrderReturnType.time,
    },
  ];
  return (
    <SelectMenu
      label={t('reservation.returnType')}
      name={name}
      values={values}
      valueAttr="value"
      labelAttr="name"
      required={true}
      control={control}
    />
  );
}

interface StandingOrderFormProps {
  appointmentTime: any;
  formHook: UseFormReturn<any, any>;
}

const RETURN_TIME_MIN_MINUTES_AFTER_APPOINTMENT = 5;

export default function StandingOrderForm({
  appointmentTime,
  formHook,
}: StandingOrderFormProps) {
  const t = useCommonTranslation();
  const enableReturnTripOverrides =
    useFeatureStandingOrderReturnSpecification();
  const estimatedTripTime = useAppSelector(
    reservationSlice.selectEstimatedTripTime,
  );

  const {
    control,
    watch,
    setValue,
    getFieldState,
    setError,
    clearErrors,
    formState: { errors },
  } = formHook;

  const [standingOrderRangeError, setStandingOrderRangeError] = useState('');

  const tripType = watch('TripType');
  const appointmentDateTime = watch('MultiLegTrips[0].AppointmentTime');
  const pickupDateTime = watch('MultiLegTrips[0].PickupTime');
  const returnIsWillCall = watch('MultiLegTrips[0].RoundTripWillCall');
  const returnTime = watch('MultiLegTrips[0].RoundTripReturnTime');
  const standingOrderStartDate = watch('StandingOrderStartDate');
  const standingOrderEndDate = watch('StandingOrderEndDate');
  const isMondaySelected = watch('StandingOrderMonday');
  const isTuesdaySelected = watch('StandingOrderTuesday');
  const isWednesdaySelected = watch('StandingOrderWednesday');
  const isThursdaySelected = watch('StandingOrderThursday');
  const isFridaySelected = watch('StandingOrderFriday');
  const isSaturdaySelected = watch('StandingOrderSaturday');
  const isSundaySelected = watch('StandingOrderSunday');
  const mondayAppointmentTime = watch('StandingOrderMondayAppointmentTime');
  const mondayPickupTime: DateTime = watch('StandingOrderMondayPickupTime');
  const mondayReturnType: StandingOrderReturnType = watch(
    'StandingOrderMondayReturnType',
  );
  const tuesdayAppointmentTime = watch('StandingOrderTuesdayAppointmentTime');
  const tuesdayPickupTime = watch('StandingOrderTuesdayPickupTime');
  const tuesdayReturnType: StandingOrderReturnType = watch(
    'StandingOrderTuesdayReturnType',
  );
  const wednesdayAppointmentTime = watch(
    'StandingOrderWednesdayAppointmentTime',
  );
  const wednesdayPickupTime = watch('StandingOrderWednesdayPickupTime');
  const wednesdayReturnType: StandingOrderReturnType = watch(
    'StandingOrderWednesdayReturnType',
  );
  const thursdayAppointmentTime = watch('StandingOrderThursdayAppointmentTime');
  const thursdayPickupTime = watch('StandingOrderThursdayPickupTime');
  const thursdayReturnType: StandingOrderReturnType = watch(
    'StandingOrderThursdayReturnType',
  );
  const fridayAppointmentTime = watch('StandingOrderFridayAppointmentTime');
  const fridayPickupTime = watch('StandingOrderFridayPickupTime');
  const fridayReturnType: StandingOrderReturnType = watch(
    'StandingOrderFridayReturnType',
  );
  const saturdayAppointmentTime = watch('StandingOrderSaturdayAppointmentTime');
  const saturdayPickupTime = watch('StandingOrderSaturdayPickupTime');
  const saturdayReturnType: StandingOrderReturnType = watch(
    'StandingOrderSaturdayReturnType',
  );
  const sundayAppointmentTime = watch('StandingOrderSundayAppointmentTime');
  const sundayPickupTime = watch('StandingOrderSundayPickupTime');
  const sundayReturnType: StandingOrderReturnType = watch(
    'StandingOrderSundayReturnType',
  );
  const [standingOrderPickups, setStandingOrderPickups] = useState('');
  const [standingOrderReservationCount, setStandingOrderReservationCount] =
    useState(0);

  const formatDayOfWeekTime = (day: string, dt: DateTime, at: string) => {
    return `${day} ${at} ${dt.toLocaleString(DateTime.TIME_SIMPLE)}`;
  };

  const calculateReservationCount = () => {
    let count = 0;
    if (standingOrderStartDate?.isValid && standingOrderEndDate?.isValid) {
      const interval = Interval.fromDateTimes(
        standingOrderStartDate.startOf('day'),
        standingOrderEndDate.plus({ days: 1 }).startOf('day'),
      );
      const dayIntervals = interval.splitBy({ days: 1 });
      const selectedDays: number[] = [];
      if (isMondaySelected) selectedDays.push(1);
      if (isTuesdaySelected) selectedDays.push(2);
      if (isWednesdaySelected) selectedDays.push(3);
      if (isThursdaySelected) selectedDays.push(4);
      if (isFridaySelected) selectedDays.push(5);
      if (isSaturdaySelected) selectedDays.push(6);
      if (isSundaySelected) selectedDays.push(7);
      count = dayIntervals.reduce((cumulative, subInterval) => {
        if (selectedDays.includes(subInterval.start?.weekday || -1)) {
          cumulative++;
        }
        return cumulative;
      }, 0);
    }
    return count;
  };

  useEffect(() => {
    if (appointmentTime?.isValid) {
      setValue('StandingOrderStartDate', appointmentTime);
    } else {
      setValue('StandingOrderStartDate', undefined);
    }
  }, [appointmentTime]);

  useEffect(() => {
    let pickups = '';
    let reservationCount = 0;
    const soPickups: string[] = [];
    if (standingOrderStartDate?.isValid && standingOrderEndDate?.isValid) {
      const at = t('reservation.at');
      if (isMondaySelected && mondayPickupTime && mondayPickupTime.isValid) {
        soPickups.push(
          formatDayOfWeekTime(t('reservation.monday'), mondayPickupTime, at),
        );
      }
      if (isTuesdaySelected && tuesdayPickupTime && tuesdayPickupTime.isValid) {
        soPickups.push(
          formatDayOfWeekTime(t('reservation.tuesday'), tuesdayPickupTime, at),
        );
      }
      if (
        isWednesdaySelected &&
        wednesdayPickupTime &&
        wednesdayPickupTime.isValid
      ) {
        soPickups.push(
          formatDayOfWeekTime(
            t('reservation.wednesday'),
            wednesdayPickupTime,
            at,
          ),
        );
      }
      if (
        isThursdaySelected &&
        thursdayPickupTime &&
        thursdayPickupTime.isValid
      ) {
        soPickups.push(
          formatDayOfWeekTime(
            t('reservation.thursday'),
            thursdayPickupTime,
            at,
          ),
        );
      }
      if (isFridaySelected && fridayPickupTime && fridayPickupTime.isValid) {
        soPickups.push(
          formatDayOfWeekTime(t('reservation.friday'), fridayPickupTime, at),
        );
      }
      if (
        isSaturdaySelected &&
        saturdayPickupTime &&
        saturdayPickupTime.isValid
      ) {
        soPickups.push(
          formatDayOfWeekTime(
            t('reservation.saturday'),
            saturdayPickupTime,
            at,
          ),
        );
      }
      if (isSundaySelected && sundayPickupTime && sundayPickupTime.isValid) {
        soPickups.push(
          formatDayOfWeekTime(t('reservation.sunday'), sundayPickupTime, at),
        );
      }
      if (soPickups.length > 0) {
        pickups = soPickups.join(', ');
      }
      reservationCount = calculateReservationCount();
    }
    setStandingOrderReservationCount(reservationCount);
    setStandingOrderPickups(pickups);
  }, [
    standingOrderStartDate,
    standingOrderEndDate,
    isMondaySelected,
    isTuesdaySelected,
    isWednesdaySelected,
    isThursdaySelected,
    isFridaySelected,
    isSaturdaySelected,
    isSundaySelected,
    standingOrderStartDate,
    standingOrderEndDate,
    mondayPickupTime,
    tuesdayPickupTime,
    wednesdayPickupTime,
    thursdayPickupTime,
    fridayPickupTime,
    saturdayPickupTime,
    sundayPickupTime,
  ]);

  useEffect(() => {
    if (appointmentDateTime) {
      if (!getFieldState('StandingOrderMondayAppointmentTime').isDirty) {
        setValue('StandingOrderMondayAppointmentTime', appointmentDateTime);
      }
      if (!getFieldState('StandingOrderTuesdayAppointmentTime').isDirty) {
        setValue('StandingOrderTuesdayAppointmentTime', appointmentDateTime);
      }
      if (!getFieldState('StandingOrderWednesdayAppointmentTime').isDirty) {
        setValue('StandingOrderWednesdayAppointmentTime', appointmentDateTime);
      }
      if (!getFieldState('StandingOrderThursdayAppointmentTime').isDirty) {
        setValue('StandingOrderThursdayAppointmentTime', appointmentDateTime);
      }
      if (!getFieldState('StandingOrderFridayAppointmentTime').isDirty) {
        setValue('StandingOrderFridayAppointmentTime', appointmentDateTime);
      }
      if (!getFieldState('StandingOrderSaturdayAppointmentTime').isDirty) {
        setValue('StandingOrderSaturdayAppointmentTime', appointmentDateTime);
      }
      if (!getFieldState('StandingOrderSundayAppointmentTime').isDirty) {
        setValue('StandingOrderSundayAppointmentTime', appointmentDateTime);
      }
    }
  }, [appointmentDateTime]);

  useEffect(() => {
    if (pickupDateTime) {
      if (!getFieldState('StandingOrderMondayPickupTime').isDirty) {
        setValue('StandingOrderMondayPickupTime', pickupDateTime);
      }
      if (!getFieldState('StandingOrderTuesdayPickupTime').isDirty) {
        setValue('StandingOrderTuesdayPickupTime', pickupDateTime);
      }
      if (!getFieldState('StandingOrderWednesdayPickupTime').isDirty) {
        setValue('StandingOrderWednesdayPickupTime', pickupDateTime);
      }
      if (!getFieldState('StandingOrderThursdayPickupTime').isDirty) {
        setValue('StandingOrderThursdayPickupTime', pickupDateTime);
      }
      if (!getFieldState('StandingOrderFridayPickupTime').isDirty) {
        setValue('StandingOrderFridayPickupTime', pickupDateTime);
      }
      if (!getFieldState('StandingOrderSaturdayPickupTime').isDirty) {
        setValue('StandingOrderSaturdayPickupTime', pickupDateTime);
      }
      if (!getFieldState('StandingOrderSundayPickupTime').isDirty) {
        setValue('StandingOrderSundayPickupTime', pickupDateTime);
      }
    }
  }, [pickupDateTime]);

  useEffect(() => {
    if (!getFieldState('StandingOrderMondayReturnPickupTime').isDirty) {
      setValue(
        'StandingOrderMondayReturnType',
        returnIsWillCall
          ? StandingOrderReturnType.willCall
          : StandingOrderReturnType.time,
      );
    }
    if (!getFieldState('StandingOrderTuesdayReturnPickupTime').isDirty) {
      setValue(
        'StandingOrderTuesdayReturnType',
        returnIsWillCall
          ? StandingOrderReturnType.willCall
          : StandingOrderReturnType.time,
      );
    }
    if (!getFieldState('StandingOrderWednesdayReturnPickupTime').isDirty) {
      setValue(
        'StandingOrderWednesdayReturnType',
        returnIsWillCall
          ? StandingOrderReturnType.willCall
          : StandingOrderReturnType.time,
      );
    }
    if (!getFieldState('StandingOrderThursdayReturnPickupTime').isDirty) {
      setValue(
        'StandingOrderThursdayReturnType',
        returnIsWillCall
          ? StandingOrderReturnType.willCall
          : StandingOrderReturnType.time,
      );
    }
    if (!getFieldState('StandingOrderFridayReturnPickupTime').isDirty) {
      setValue(
        'StandingOrderFridayReturnType',
        returnIsWillCall
          ? StandingOrderReturnType.willCall
          : StandingOrderReturnType.time,
      );
    }
    if (!getFieldState('StandingOrderSaturdayReturnPickupTime').isDirty) {
      setValue(
        'StandingOrderSaturdayReturnType',
        returnIsWillCall
          ? StandingOrderReturnType.willCall
          : StandingOrderReturnType.time,
      );
    }
    if (!getFieldState('StandingOrderSundayReturnPickupTime').isDirty) {
      setValue(
        'StandingOrderSundayReturnType',
        returnIsWillCall
          ? StandingOrderReturnType.willCall
          : StandingOrderReturnType.time,
      );
    }
  }, [returnIsWillCall]);

  useEffect(() => {
    if (!getFieldState('StandingOrderMondayReturnPickupTime').isDirty) {
      setValue('StandingOrderMondayReturnPickupTime', returnTime);
    }
    if (!getFieldState('StandingOrderTuesdayReturnPickupTime').isDirty) {
      setValue('StandingOrderTuesdayReturnPickupTime', returnTime);
    }
    if (!getFieldState('StandingOrderWednesdayReturnPickupTime').isDirty) {
      setValue('StandingOrderWednesdayReturnPickupTime', returnTime);
    }
    if (!getFieldState('StandingOrderThursdayReturnPickupTime').isDirty) {
      setValue('StandingOrderThursdayReturnPickupTime', returnTime);
    }
    if (!getFieldState('StandingOrderFridayReturnPickupTime').isDirty) {
      setValue('StandingOrderFridayReturnPickupTime', returnTime);
    }
    if (!getFieldState('StandingOrderSaturdayReturnPickupTime').isDirty) {
      setValue('StandingOrderSaturdayReturnPickupTime', returnTime);
    }
    if (!getFieldState('StandingOrderSundayReturnPickupTime').isDirty) {
      setValue('StandingOrderSundayReturnPickupTime', returnTime);
    }
  }, [returnTime]);

  useEffect(() => {
    let msg = '';
    if (standingOrderEndDate && standingOrderStartDate) {
      const daysDiff = standingOrderEndDate.diff(
        standingOrderStartDate,
        'days',
      ).days;
      if (daysDiff <= 0) {
        msg = t('reservation.endDateMustBeAfterStartDate');
      } else if (daysDiff > 90) {
        msg = t('reservation.max90');
      }
    }
    setStandingOrderRangeError(msg);
  }, [standingOrderStartDate, standingOrderEndDate]);

  useEffect(() => {
    if (isMondaySelected && estimatedTripTime) {
      if (!mondayAppointmentTime?.isValid) {
        if (appointmentTime?.isValid) {
          setValue('StandingOrderMondayAppointmentTime', appointmentTime);
          setValue(
            'StandingOrderMondayPickupTime',
            appointmentTime.minus({
              minutes: estimatedTripTime.totalPickupTime,
            }),
          );
        }
      }
    }
  }, [isMondaySelected]);
  useEffect(() => {
    if (isTuesdaySelected && estimatedTripTime) {
      if (!tuesdayAppointmentTime?.isValid) {
        if (appointmentTime?.isValid) {
          setValue('StandingOrderTuesdayAppointmentTime', appointmentTime);
          setValue(
            'StandingOrderTuesdayPickupTime',
            appointmentTime.minus({
              minutes: estimatedTripTime.totalPickupTime,
            }),
          );
        }
      }
    }
  }, [isTuesdaySelected]);
  useEffect(() => {
    if (isWednesdaySelected && estimatedTripTime) {
      if (!wednesdayAppointmentTime?.isValid) {
        if (appointmentTime?.isValid) {
          setValue('StandingOrderWednesdayAppointmentTime', appointmentTime);
          setValue(
            'StandingOrderWednesdayPickupTime',
            appointmentTime.minus({
              minutes: estimatedTripTime.totalPickupTime,
            }),
          );
        }
      }
    }
  }, [isWednesdaySelected]);
  useEffect(() => {
    if (isThursdaySelected && estimatedTripTime) {
      if (!thursdayAppointmentTime?.isValid) {
        if (appointmentTime?.isValid) {
          setValue('StandingOrderThursdayAppointmentTime', appointmentTime);
          setValue(
            'StandingOrderThursdayPickupTime',
            appointmentTime.minus({
              minutes: estimatedTripTime.totalPickupTime,
            }),
          );
        }
      }
    }
  }, [isThursdaySelected]);
  useEffect(() => {
    if (isFridaySelected && estimatedTripTime) {
      if (!fridayAppointmentTime?.isValid) {
        if (appointmentTime?.isValid) {
          setValue('StandingOrderFridayAppointmentTime', appointmentTime);
          setValue(
            'StandingOrderFridayPickupTime',
            appointmentTime.minus({
              minutes: estimatedTripTime.totalPickupTime,
            }),
          );
        }
      }
    }
  }, [isFridaySelected]);
  useEffect(() => {
    if (isSaturdaySelected && estimatedTripTime) {
      if (!saturdayAppointmentTime?.isValid) {
        if (appointmentTime?.isValid) {
          setValue('StandingOrderSaturdayAppointmentTime', appointmentTime);
          setValue(
            'StandingOrderSaturdayPickupTime',
            appointmentTime.minus({
              minutes: estimatedTripTime.totalPickupTime,
            }),
          );
        }
      }
    }
  }, [isSaturdaySelected]);
  useEffect(() => {
    if (isSundaySelected && estimatedTripTime) {
      if (!sundayAppointmentTime?.isValid) {
        if (appointmentTime?.isValid) {
          setValue('StandingOrderSundayAppointmentTime', appointmentTime);
          setValue(
            'StandingOrderSundayPickupTime',
            appointmentTime.minus({
              minutes: estimatedTripTime.totalPickupTime,
            }),
          );
        }
      }
    }
  }, [isSundaySelected]);
  const getStandingOrderDayError = (appt: DateTime, pickup: DateTime) => {
    let msg = undefined;
    if (appt?.isValid && pickup?.isValid && estimatedTripTime) {
      const diff = appt.diff(pickup, 'minutes');
      if (diff.minutes < estimatedTripTime.totalPickupTime) {
        msg = t('reservation.pickupError', {
          minutes: estimatedTripTime.totalPickupTime,
        });
      }
    }
    return msg;
  };
  useEffect(() => {
    const err = getStandingOrderDayError(
      mondayAppointmentTime,
      mondayPickupTime,
    );
    if (err) {
      setError('MondayPickupTimeError', { type: 'custom', message: err });
    } else {
      clearErrors('MondayPickupTimeError');
    }
  }, [mondayAppointmentTime, mondayPickupTime]);
  useEffect(() => {
    const err = getStandingOrderDayError(
      tuesdayAppointmentTime,
      tuesdayPickupTime,
    );
    if (err) {
      setError('TuesdayPickupTimeError', { type: 'custom', message: err });
    } else {
      clearErrors('TuesdayPickupTimeError');
    }
  }, [tuesdayAppointmentTime, tuesdayPickupTime]);
  useEffect(() => {
    const err = getStandingOrderDayError(
      wednesdayAppointmentTime,
      wednesdayPickupTime,
    );
    if (err) {
      setError('WednesdayPickupTimeError', { type: 'custom', message: err });
    } else {
      clearErrors('WednesdayPickupTimeError');
    }
  }, [wednesdayAppointmentTime, wednesdayPickupTime]);
  useEffect(() => {
    const err = getStandingOrderDayError(
      thursdayAppointmentTime,
      thursdayPickupTime,
    );
    if (err) {
      setError('ThursdayPickupTimeError', { type: 'custom', message: err });
    } else {
      clearErrors('ThursdayPickupTimeError');
    }
  }, [thursdayAppointmentTime, thursdayPickupTime]);
  useEffect(() => {
    const err = getStandingOrderDayError(
      fridayAppointmentTime,
      fridayPickupTime,
    );
    if (err) {
      setError('FridayPickupTimeError', { type: 'custom', message: err });
    } else {
      clearErrors('FridayPickupTimeError');
    }
  }, [fridayAppointmentTime, fridayPickupTime]);
  useEffect(() => {
    const err = getStandingOrderDayError(
      saturdayAppointmentTime,
      saturdayPickupTime,
    );
    if (err) {
      setError('SaturdayPickupTimeError', { type: 'custom', message: err });
    } else {
      clearErrors('SaturdayPickupTimeError');
    }
  }, [saturdayAppointmentTime, saturdayPickupTime]);
  useEffect(() => {
    const err = getStandingOrderDayError(
      sundayAppointmentTime,
      sundayPickupTime,
    );
    if (err) {
      setError('SundayPickupTimeError', { type: 'custom', message: err });
    } else {
      clearErrors('SundayPickupTimeError');
    }
  }, [sundayAppointmentTime, sundayPickupTime]);

  return (
    <StyledBox>
      <Typography variant="body2" className={classes.standingOrderDescription}>
        {t('reservation.standingOrderDescription')}
      </Typography>
      <Box className={classes.standingOrderRange}>
        <DateInput
          label={t('reservation.starts')}
          name="StandingOrderStartDate"
          className={classes.standingOrderDate}
          control={control}
          required={true}
          timezone={dateUtils.WORK_TIME_ZONE}
          disabled={true}
        />
        <Box className={classes.standingOrderDate}>
          <DateInput
            label={t('reservation.ends')}
            name="StandingOrderEndDate"
            control={control}
            required={true}
            timezone={dateUtils.WORK_TIME_ZONE}
            minDate={standingOrderStartDate?.plus({ days: 1 }).startOf('day')}
            maxDate={
              standingOrderStartDate
                ? standingOrderStartDate.plus({ days: 90 })
                : undefined
            }
          />
          <FormHelperText error>{standingOrderRangeError}</FormHelperText>
        </Box>
      </Box>
      <Grid
        container
        columnSpacing={2}
        rowSpacing={1}
        className={classes.standingOrderGrid}
        alignItems="flex-start">
        <Grid item xs={4}>
          {t('reservation.days')}
        </Grid>
        <Grid item xs={4}>
          {t('reservation.appointment')}
        </Grid>
        <Grid item xs={4}>
          {t('reservation.pickup')}
        </Grid>
        <Grid item xs={12}>
          <Divider />
        </Grid>
        <Grid item xs={4}>
          <Checkbox
            name="StandingOrderMonday"
            label={t('reservation.monday')}
            control={control}
          />
        </Grid>
        <Grid item xs={4}>
          {isMondaySelected && (
            <TimeInput
              label={t('reservation.appointment')}
              name="StandingOrderMondayAppointmentTime"
              control={control}
              required={true}
              timezone={dateUtils.WORK_TIME_ZONE}
              disabled={tripType === TripLegType.RoundTrip}
            />
          )}
        </Grid>
        <Grid item xs={4}>
          {isMondaySelected && (
            <>
              <TimeInput
                label={t('reservation.pickup')}
                name="StandingOrderMondayPickupTime"
                control={control}
                required={true}
                timezone={dateUtils.WORK_TIME_ZONE}
                disabled={tripType === TripLegType.RoundTrip}
              />
              <FormHelperText error>
                {(errors as any).MondayPickupTimeError?.message}
              </FormHelperText>
            </>
          )}
        </Grid>
        {enableReturnTripOverrides &&
          tripType === TripLegType.RoundTrip &&
          isMondaySelected && (
            <>
              <Grid item xs={4}></Grid>
              <Grid item xs={4}>
                <PickupTypeSelectMenu
                  name="StandingOrderMondayReturnType"
                  control={control}
                />
              </Grid>
              <Grid item xs={4}>
                {mondayReturnType === StandingOrderReturnType.time && (
                  <>
                    <TimeInput
                      label={t('reservation.returnPickup')}
                      name="StandingOrderMondayReturnPickupTime"
                      control={control}
                      required={true}
                      timezone={dateUtils.WORK_TIME_ZONE}
                      minTime={mondayAppointmentTime.plus({
                        minutes: RETURN_TIME_MIN_MINUTES_AFTER_APPOINTMENT,
                      })}
                    />
                  </>
                )}
              </Grid>
            </>
          )}
        <Grid item xs={4}>
          <Checkbox
            name="StandingOrderTuesday"
            label={t('reservation.tuesday')}
            control={control}
          />
        </Grid>
        <Grid item xs={4}>
          {isTuesdaySelected && (
            <TimeInput
              label=" "
              name="StandingOrderTuesdayAppointmentTime"
              control={control}
              required={true}
              timezone={dateUtils.WORK_TIME_ZONE}
              disabled={tripType === TripLegType.RoundTrip}
            />
          )}
        </Grid>
        <Grid item xs={4}>
          {isTuesdaySelected && (
            <>
              <TimeInput
                label=" "
                name="StandingOrderTuesdayPickupTime"
                control={control}
                required={true}
                timezone={dateUtils.WORK_TIME_ZONE}
                disabled={tripType === TripLegType.RoundTrip}
              />
              <FormHelperText error>
                {(errors as any).TuesdayPickupTimeError?.message}
              </FormHelperText>
            </>
          )}
        </Grid>
        {enableReturnTripOverrides &&
          tripType === TripLegType.RoundTrip &&
          isTuesdaySelected && (
            <>
              <Grid item xs={4}></Grid>
              <Grid item xs={4}>
                <PickupTypeSelectMenu
                  name="StandingOrderTuesdayReturnType"
                  control={control}
                />
              </Grid>
              <Grid item xs={4}>
                {tuesdayReturnType === StandingOrderReturnType.time && (
                  <>
                    <TimeInput
                      label={t('reservation.returnPickup')}
                      name="StandingOrderTuesdayReturnPickupTime"
                      control={control}
                      required={true}
                      timezone={dateUtils.WORK_TIME_ZONE}
                      minTime={tuesdayAppointmentTime.plus({
                        minutes: RETURN_TIME_MIN_MINUTES_AFTER_APPOINTMENT,
                      })}
                    />
                  </>
                )}
              </Grid>
            </>
          )}
        <Grid item xs={4}>
          <Checkbox
            name="StandingOrderWednesday"
            label={t('reservation.wednesday')}
            control={control}
          />
        </Grid>
        <Grid item xs={4}>
          {isWednesdaySelected && (
            <TimeInput
              label=" "
              name="StandingOrderWednesdayAppointmentTime"
              control={control}
              required={true}
              timezone={dateUtils.WORK_TIME_ZONE}
              disabled={tripType === TripLegType.RoundTrip}
            />
          )}
        </Grid>
        <Grid item xs={4}>
          {isWednesdaySelected && (
            <>
              <TimeInput
                label=" "
                name="StandingOrderWednesdayPickupTime"
                control={control}
                required={true}
                timezone={dateUtils.WORK_TIME_ZONE}
                disabled={tripType === TripLegType.RoundTrip}
              />
              <FormHelperText error>
                {(errors as any).WednesdayPickupTimeError?.message}
              </FormHelperText>
            </>
          )}
        </Grid>
        {enableReturnTripOverrides &&
          tripType === TripLegType.RoundTrip &&
          isWednesdaySelected && (
            <>
              <Grid item xs={4}></Grid>
              <Grid item xs={4}>
                <PickupTypeSelectMenu
                  name="StandingOrderWednesdayReturnType"
                  control={control}
                />
              </Grid>
              <Grid item xs={4}>
                {wednesdayReturnType === StandingOrderReturnType.time && (
                  <>
                    <TimeInput
                      label={t('reservation.returnPickup')}
                      name="StandingOrderWednesdayReturnPickupTime"
                      control={control}
                      required={true}
                      timezone={dateUtils.WORK_TIME_ZONE}
                      minTime={wednesdayAppointmentTime.plus({
                        minutes: RETURN_TIME_MIN_MINUTES_AFTER_APPOINTMENT,
                      })}
                    />
                  </>
                )}
              </Grid>
            </>
          )}
        <Grid item xs={4}>
          <Checkbox
            name="StandingOrderThursday"
            label={t('reservation.thursday')}
            control={control}
          />
        </Grid>
        <Grid item xs={4}>
          {isThursdaySelected && (
            <TimeInput
              label=" "
              name="StandingOrderThursdayAppointmentTime"
              control={control}
              required={true}
              timezone={dateUtils.WORK_TIME_ZONE}
              disabled={tripType === TripLegType.RoundTrip}
            />
          )}
        </Grid>
        <Grid item xs={4}>
          {isThursdaySelected && (
            <>
              <TimeInput
                label=" "
                name="StandingOrderThursdayPickupTime"
                control={control}
                required={true}
                timezone={dateUtils.WORK_TIME_ZONE}
                disabled={tripType === TripLegType.RoundTrip}
              />
              <FormHelperText error>
                {(errors as any).ThursdayPickupTimeError?.message}
              </FormHelperText>
            </>
          )}
        </Grid>
        {enableReturnTripOverrides &&
          tripType === TripLegType.RoundTrip &&
          isThursdaySelected && (
            <>
              <Grid item xs={4}></Grid>
              <Grid item xs={4}>
                <PickupTypeSelectMenu
                  name="StandingOrderThursdayReturnType"
                  control={control}
                />
              </Grid>
              <Grid item xs={4}>
                {thursdayReturnType === StandingOrderReturnType.time && (
                  <>
                    <TimeInput
                      label={t('reservation.returnPickup')}
                      name="StandingOrderThursdayReturnPickupTime"
                      control={control}
                      required={true}
                      timezone={dateUtils.WORK_TIME_ZONE}
                      minTime={thursdayAppointmentTime.plus({
                        minutes: RETURN_TIME_MIN_MINUTES_AFTER_APPOINTMENT,
                      })}
                    />
                  </>
                )}
              </Grid>
            </>
          )}
        <Grid item xs={4}>
          <Checkbox
            name="StandingOrderFriday"
            label={t('reservation.friday')}
            control={control}
          />
        </Grid>
        <Grid item xs={4}>
          {isFridaySelected && (
            <TimeInput
              label=" "
              name="StandingOrderFridayAppointmentTime"
              control={control}
              required={true}
              timezone={dateUtils.WORK_TIME_ZONE}
              disabled={tripType === TripLegType.RoundTrip}
            />
          )}
        </Grid>
        <Grid item xs={4}>
          {isFridaySelected && (
            <>
              <TimeInput
                label=" "
                name="StandingOrderFridayPickupTime"
                control={control}
                required={true}
                timezone={dateUtils.WORK_TIME_ZONE}
                disabled={tripType === TripLegType.RoundTrip}
              />
              <FormHelperText error>
                {(errors as any).FridayPickupTimeError?.message}
              </FormHelperText>
            </>
          )}
        </Grid>
        {enableReturnTripOverrides &&
          tripType === TripLegType.RoundTrip &&
          isFridaySelected && (
            <>
              <Grid item xs={4}></Grid>
              <Grid item xs={4}>
                <PickupTypeSelectMenu
                  name="StandingOrderFridayReturnType"
                  control={control}
                />
              </Grid>
              <Grid item xs={4}>
                {fridayReturnType === StandingOrderReturnType.time && (
                  <>
                    <TimeInput
                      label={t('reservation.returnPickup')}
                      name="StandingOrderFridayReturnPickupTime"
                      control={control}
                      required={true}
                      timezone={dateUtils.WORK_TIME_ZONE}
                      minTime={fridayAppointmentTime.plus({
                        minutes: RETURN_TIME_MIN_MINUTES_AFTER_APPOINTMENT,
                      })}
                    />
                  </>
                )}
              </Grid>
            </>
          )}
        <Grid item xs={4}>
          <Checkbox
            name="StandingOrderSaturday"
            label={t('reservation.saturday')}
            control={control}
          />
        </Grid>
        <Grid item xs={4}>
          {isSaturdaySelected && (
            <TimeInput
              label=" "
              name="StandingOrderSaturdayAppointmentTime"
              control={control}
              required={true}
              timezone={dateUtils.WORK_TIME_ZONE}
              disabled={tripType === TripLegType.RoundTrip}
            />
          )}
        </Grid>
        <Grid item xs={4}>
          {isSaturdaySelected && (
            <>
              <TimeInput
                label=" "
                name="StandingOrderSaturdayPickupTime"
                control={control}
                required={true}
                timezone={dateUtils.WORK_TIME_ZONE}
                disabled={tripType === TripLegType.RoundTrip}
              />
              <FormHelperText error>
                {(errors as any).SaturdayPickupTimeError?.message}
              </FormHelperText>
            </>
          )}
        </Grid>
        {enableReturnTripOverrides &&
          tripType === TripLegType.RoundTrip &&
          isSaturdaySelected && (
            <>
              <Grid item xs={4}></Grid>
              <Grid item xs={4}>
                <PickupTypeSelectMenu
                  name="StandingOrderSaturdayReturnType"
                  control={control}
                />
              </Grid>
              <Grid item xs={4}>
                {saturdayReturnType === StandingOrderReturnType.time && (
                  <>
                    <TimeInput
                      label={t('reservation.returnPickup')}
                      name="StandingOrderSaturdayReturnPickupTime"
                      control={control}
                      required={true}
                      timezone={dateUtils.WORK_TIME_ZONE}
                      minTime={saturdayAppointmentTime.plus({
                        minutes: RETURN_TIME_MIN_MINUTES_AFTER_APPOINTMENT,
                      })}
                    />
                  </>
                )}
              </Grid>
            </>
          )}
        <Grid item xs={4}>
          <Checkbox
            name="StandingOrderSunday"
            label={t('reservation.sunday')}
            control={control}
          />
        </Grid>
        <Grid item xs={4}>
          {isSundaySelected && (
            <TimeInput
              label=" "
              name="StandingOrderSundayAppointmentTime"
              control={control}
              required={true}
              timezone={dateUtils.WORK_TIME_ZONE}
              disabled={tripType === TripLegType.RoundTrip}
            />
          )}
        </Grid>
        <Grid item xs={4}>
          {isSundaySelected && (
            <>
              <TimeInput
                label=" "
                name="StandingOrderSundayPickupTime"
                control={control}
                required={true}
                timezone={dateUtils.WORK_TIME_ZONE}
                disabled={tripType === TripLegType.RoundTrip}
              />
              <FormHelperText error>
                {(errors as any).SundayPickupTimeError?.message}
              </FormHelperText>
            </>
          )}
        </Grid>
        {enableReturnTripOverrides &&
          tripType === TripLegType.RoundTrip &&
          isSundaySelected && (
            <>
              <Grid item xs={4}></Grid>
              <Grid item xs={4}>
                <PickupTypeSelectMenu
                  name="StandingOrderSundayReturnType"
                  control={control}
                />
              </Grid>
              <Grid item xs={4}>
                {sundayReturnType === StandingOrderReturnType.time && (
                  <>
                    <TimeInput
                      label={t('reservation.returnPickup')}
                      name="StandingOrderSundayReturnPickupTime"
                      control={control}
                      required={true}
                      timezone={dateUtils.WORK_TIME_ZONE}
                      minTime={sundayAppointmentTime.plus({
                        minutes: RETURN_TIME_MIN_MINUTES_AFTER_APPOINTMENT,
                      })}
                    />
                  </>
                )}
              </Grid>
            </>
          )}
      </Grid>
      {standingOrderPickups && (
        <Typography className={classes.standingOrderRecap}>
          {t('reservation.booked')}{' '}
          <Box component="span" className={classes.standingOrderRecapBold}>
            {standingOrderPickups}
          </Box>{' '}
          {t('reservation.from')}{' '}
          <Box component="span" className={classes.standingOrderRecapBold}>
            {standingOrderStartDate.toLocaleString(DateTime.DATE_SHORT)}
          </Box>{' '}
          {t('reservation.until')}{' '}
          <Box component="span" className={classes.standingOrderRecapBold}>
            {standingOrderEndDate.toLocaleString(DateTime.DATE_SHORT)}
          </Box>{' '}
          {t('reservation.reservations', {
            count: standingOrderReservationCount,
          })}
        </Typography>
      )}
    </StyledBox>
  );
}
